import { useState, useMemo } from 'react'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card'
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select'
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover'
import { Calendar } from '@/components/ui/calendar'
import { Button } from '@/components/ui/button'
import { CalendarIcon, RefreshCw, TrendingUp, TrendingDown, Minus } from 'lucide-react'
import { format, subDays } from 'date-fns'
import { zhTW } from 'date-fns/locale'
import { Skeleton } from '@/components/ui/skeleton'
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
} from 'recharts'
import { DateRange } from 'react-day-picker'
import { useClinicPatientFlowRecords } from '@/hooks/useClinicPatientFlowRecords'
import { useClinicPatientFlowStats } from '@/hooks/useClinicPatientFlowStats'

interface Props {
  clinicId: number
}

interface PatientFlowDailyRecord {
  flow_daily_id: number
  clinic_id: number
  date: string
  patient_count: number
  created_at: string
  updated_at: string
}

interface PatientFlowHourlyRecord {
  flow_hourly_id: number
  clinic_id: number
  snapshot_hour: string
  patient_count: number
  created_at: string
  updated_at: string
}

export default function ClinicPatientFlowInfo({ clinicId }: Props) {
  const [dateRange, setDateRange] = useState<DateRange>({
    from: subDays(new Date(), 7),
    to: new Date(),
  })
  const [calendarOpen, setCalendarOpen] = useState(false)
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d'>('7d')
  const [selectedDate, setSelectedDate] = useState<string | null>(null)

  // 根據時間範圍設定日期
  const handleTimeRangeChange = (range: '7d' | '30d' | '90d') => {
    setTimeRange(range)
    const today = new Date()
    const days = range === '7d' ? 7 : range === '30d' ? 30 : 90
    setDateRange({
      from: subDays(today, days),
      to: today,
    })
  }

  const dailyParams = useMemo(
    () => ({
      clinicId,
      startDate: dateRange.from ? format(dateRange.from, 'yyyy-MM-dd') : undefined,
      endDate: dateRange.to ? format(dateRange.to, 'yyyy-MM-dd') : undefined,
      interval: 'daily' as const,
      pageSize: 100,
    }),
    [clinicId, dateRange],
  )

  const {
    patientFlowRecords: dailyRecords,
    isLoading: isDailyLoading,
    mutate: refreshDaily,
  } = useClinicPatientFlowRecords(dailyParams)

  const { flowStats, isLoading: isStatsLoading } = useClinicPatientFlowStats(clinicId)

  // Hourly data hook - only fetches when a date is selected
  const hourlyParams = useMemo(
    () => ({
      clinicId,
      interval: 'hourly' as const,
      date: selectedDate || undefined,
    }),
    [clinicId, selectedDate],
  )

  const {
    patientFlowRecords: hourlyRecords,
    isLoading: isHourlyLoading,
  } = useClinicPatientFlowRecords(hourlyParams)

  const isDailyRecord = (
    r: PatientFlowDailyRecord | PatientFlowHourlyRecord,
  ): r is PatientFlowDailyRecord => 'date' in r && 'flow_daily_id' in r
  const isHourlyRecord = (
    r: PatientFlowDailyRecord | PatientFlowHourlyRecord,
  ): r is PatientFlowHourlyRecord => 'snapshot_hour' in r && 'flow_hourly_id' in r

  const dailyChartData = useMemo(() => {
    if (!dateRange.from || !dateRange.to || !dailyRecords) return []

    const allDates: Date[] = []
    const cur = new Date(dateRange.from)
    const end = new Date(dateRange.to)
    const maxDays = 90
    let cnt = 0
    while (cur <= end && cnt < maxDays) {
      allDates.push(new Date(cur))
      cur.setDate(cur.getDate() + 1)
      cnt++
    }

    const map = new Map<string, number>()
    const filteredRecords = dailyRecords.filter(isDailyRecord)

    filteredRecords.forEach((rec) => {
      // 處理日期格式 - API 返回的是 ISO 字符串
      let dateStr: string
      if (typeof rec.date === 'string') {
        // 如果是字符串，直接使用前10個字符 (YYYY-MM-DD)
        dateStr = rec.date.substring(0, 10)
      } else {
        // 如果是Date對象，格式化為YYYY-MM-DD
        dateStr = format(new Date(rec.date), 'yyyy-MM-dd')
      }
      map.set(dateStr, rec.patient_count)
    })

    return allDates
      .map((d) => {
        const dstr = format(d, 'yyyy-MM-dd')
        const count = map.get(dstr) || 0
        return { date: format(d, 'MM/dd'), fullDate: dstr, count }
      })
      .sort((a, b) => new Date(a.fullDate).getTime() - new Date(b.fullDate).getTime())
  }, [dateRange, dailyRecords])

  // Hourly chart data - 24 slots (00:00 - 23:00)
  const hourlyChartData = useMemo(() => {
    if (!selectedDate) return []

    const recordMap = new Map<number, number>()
    hourlyRecords.filter(isHourlyRecord).forEach((record) => {
      const hour = new Date(record.snapshot_hour).getUTCHours()
      recordMap.set(hour, record.patient_count)
    })

    return Array.from({ length: 24 }, (_, hour) => ({
      hour: `${hour.toString().padStart(2, '0')}:00`,
      count: recordMap.get(hour) ?? 0,
    }))
  }, [selectedDate, hourlyRecords])

  // Daily bar click handler
  const handleDailyBarClick = (data: { date: string; fullDate: string; count: number }) => {
    if (data?.fullDate) {
      setSelectedDate((prev) => (prev === data.fullDate ? null : data.fullDate))
    }
  }

  // 計算成長率
  const growthRate = useMemo(() => {
    if (!flowStats || flowStats.yesterday === 0) return 0
    return ((flowStats.today - flowStats.yesterday) / flowStats.yesterday) * 100
  }, [flowStats])

  const getTrendIcon = () => {
    if (growthRate > 0) {
      return <TrendingUp className="h-4 w-4 text-green-500" />
    } else if (growthRate < 0) {
      return <TrendingDown className="h-4 w-4 text-red-500" />
    }
    return <Minus className="h-4 w-4 text-gray-400" />
  }

  const getTrendColor = () => {
    if (growthRate > 0) return 'text-green-600'
    if (growthRate < 0) return 'text-red-600'
    return 'text-gray-600'
  }

  const formatNumber = (num: number) => {
    return num.toLocaleString('zh-TW')
  }

  return (
    <div className="space-y-6">
      {/* 今日/昨日統計卡片 */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">今日人流</CardTitle>
          </CardHeader>
          <CardContent>
            {isStatsLoading ? (
              <Skeleton className="h-8 w-20" />
            ) : (
              <div className="text-2xl font-bold text-gray-900">
                {formatNumber(flowStats?.today || 0)}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">昨日人流</CardTitle>
          </CardHeader>
          <CardContent>
            {isStatsLoading ? (
              <Skeleton className="h-8 w-20" />
            ) : (
              <div className="text-2xl font-bold text-gray-700">
                {formatNumber(flowStats?.yesterday || 0)}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">日成長率</CardTitle>
          </CardHeader>
          <CardContent>
            {isStatsLoading ? (
              <Skeleton className="h-8 w-20" />
            ) : (
              <div className="flex items-center gap-2">
                {getTrendIcon()}
                <span className={`text-xl font-bold ${getTrendColor()}`}>
                  {Math.abs(growthRate).toFixed(1)}%
                </span>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* 歷史趨勢圖表 */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">診所人流統計資料</CardTitle>
          <CardDescription>查看診所每日人流數據（資料來源：Health Online API）</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-2">
              <Select
                value={timeRange}
                onValueChange={(v: string) => handleTimeRangeChange(v as '7d' | '30d' | '90d')}
              >
                <SelectTrigger className="w-[120px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7d">過去7天</SelectItem>
                  <SelectItem value="30d">過去30天</SelectItem>
                  <SelectItem value="90d">過去90天</SelectItem>
                </SelectContent>
              </Select>
              <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-[240px] justify-start text-left">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {dateRange.from
                      ? dateRange.to
                        ? `${format(dateRange.from, 'yyyy-MM-dd')} - ${format(dateRange.to, 'yyyy-MM-dd')}`
                        : format(dateRange.from, 'yyyy-MM-dd')
                      : '選擇日期範圍'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-auto p-0 bg-popover">
                  <Calendar
                    mode="range"
                    selected={dateRange}
                    onSelect={(rng) => {
                      if (rng?.from) {
                        setDateRange({ from: rng.from, to: rng.to || rng.from })
                        if (rng.to || rng.from) {
                          setCalendarOpen(false)
                        }
                      }
                    }}
                    numberOfMonths={2}
                    locale={zhTW}
                  />
                </PopoverContent>
              </Popover>
            </div>
            <Button
              variant="outline"
              size="icon"
              onClick={() => {
                refreshDaily()
              }}
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
          <div className="mt-4">
            {isDailyLoading ? (
              <Skeleton className="h-[300px] w-full" />
            ) : dailyChartData.length === 0 ? (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                暫無數據
              </div>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={dailyChartData}
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    onClick={(state) => {
                      if (state?.activePayload?.[0]?.payload) {
                        handleDailyBarClick(state.activePayload[0].payload)
                      }
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="count" name="人數">
                      {dailyChartData.map((entry) => (
                        <Cell
                          key={entry.fullDate}
                          fill={entry.fullDate === selectedDate ? '#6366f1' : '#8884d8'}
                          stroke={entry.fullDate === selectedDate ? '#4338ca' : undefined}
                          strokeWidth={entry.fullDate === selectedDate ? 2 : 0}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                <p className="text-xs text-muted-foreground mt-1 text-center">
                  {selectedDate
                    ? `已選取 ${selectedDate}，點擊同一日期可取消`
                    : '點擊圖表上的日期柱狀可查看該日時段人流'}
                </p>
              </>
            )}
          </div>
          {/* 時段人流圖表 - 點擊日期後展開 */}
          {selectedDate && (
            <div className="mt-6">
              <h4 className="text-sm font-medium text-gray-700 mb-3">
                {selectedDate} 時段人流
              </h4>
              {isHourlyLoading ? (
                <Skeleton className="h-[250px] w-full" />
              ) : hourlyChartData.every((d) => d.count === 0) ? (
                <div className="h-[250px] flex items-center justify-center text-muted-foreground border rounded-md">
                  該日暫無時段資料
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart
                    data={hourlyChartData}
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="hour" interval={1} fontSize={11} />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="count" name="人數" fill="#8884d8" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
            <Card>
              <CardHeader>
                <CardTitle>平均每日人流</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {dailyChartData.length > 0
                    ? Math.round(
                        dailyChartData.reduce((s, i) => s + i.count, 0) / dailyChartData.length,
                      )
                    : 0}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>最高日人流</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {dailyChartData.length > 0 ? Math.max(...dailyChartData.map((i) => i.count)) : 0}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>總計人流</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {dailyChartData.reduce((s, i) => s + i.count, 0)}
                </div>
              </CardContent>
            </Card>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
